import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EnvironmentActions } from '@app/core/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import * as fromRoot from '@app/reducers';
import { isUploadStatusBusy, isValidStatusToStartUpload, shouldResumeUpload } from '@app/shared/helpers/uploader';
import { Customization } from '@app/shared/models/customization';
import { TransferSettings } from '@app/shared/models/domainSettings';
import { Identity } from '@app/shared/models/identity';
import { Promotion } from '@app/shared/models/promotion';
import { Transfer } from '@app/shared/models/transfer';
import { DeliveryType } from '@app/shared/models/transferConfiguration';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { GetEmailChallengeOutput } from '@smash-sdk/kyc/ejs/11-2023';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { UploaderStatus } from '@smash-sdk/uploader';
import moment from 'moment';
import { Observable, Subscription, combineLatest, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, first, last, map, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { TransferCommonParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions, UploadActions } from '@app/client/uploader/store/actions';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { ModalSizePremiumComponent } from '../modals/modal-size-premium/modal-size-premium.component';
import { EmailFormComponent } from './email-form/email-form.component';
import { EmailValidationComponent } from './email-validation/email-validation.component';
import { LinkFormComponent } from './link-form/link-form.component';
import { ResolveCustomizationService } from '@app/core/services/resolvers/resolve-customization';
import { ResolveExternalPromotionService } from '@app/core/services/resolvers/resolve-external-promotion';
import { removeTagFromEmail } from '@app/shared/helpers/validators';

@Component({
  selector: 'app-upload-parameters-box',
  templateUrl: './upload-parameters-box.component.html',
  styleUrls: ['./upload-parameters-box.component.scss']
})
export class UploadParametersBoxComponent implements OnInit, OnDestroy {

  @Input() dropbox?: string;
  // Synchronous, route-derived truth (see PrepareComponent#isDropboxId) for whether this is a
  // drop-link upload — unlike `dropbox` (the async GetDropboxPreview result), it is correct from
  // the very first change detection, so the form-mode branch below never renders the standard
  // Email form (with its receivers field) during the preview's loading window.
  @Input() isDropboxId?: boolean;
  @Input() uploadFormMode: DeliveryType;
  @Input() canConfigSmash: boolean;
  @Input() transferSettings: TransferSettings;

  @ViewChild('emailForm', { static: false }) emailForm: EmailFormComponent;
  @ViewChild('linkForm', { static: false }) linkForm: LinkFormComponent;
  @ViewChild('emailValidation') emailValidationComponent: EmailValidationComponent;

  identity$: Observable<Identity>;
  promotions: Promotion[];
  promotionSelected: Promotion;

  // The drawer's design thumbnail reads the STORE, not a snapshot. It used to be bound to a
  // `customization` field read once from resolveCustomizationService, so saving a new background in
  // the Design modal (which writes to the store) left the thumbnail showing the old one -- step 2 of
  // the customer report, and the reason the transfer fix alone would have left the UI lying the
  // other way round: previewing the old background while sending the new one.
  //
  // The normalization is load-bearing. On a domain with NO branding, loadCustomizationSuccess$
  // dispatches SetTransferCustomization({logo: undefined, background: undefined}) and the reducer
  // merges that onto a null state, storing a TRUTHY husk. Bound raw, that husk keeps
  // `customizationSelected || customizationLoading` true forever, so the design tile renders (empty
  // and dead) with an edit pencil onto empty background/logo lists.
  //
  // Discriminated on `id`, NOT on "has a logo or background": a customization record exists before
  // any asset is attached to it (the model requires id/name/description and leaves logo/background
  // optional), so a freshly configured domain legitimately holds a real, asset-less customization —
  // and hiding its tile would remove the sender's only way to pick a background. The husk is built
  // by merging onto null, so it never carries an id; that separates the two exactly.
  //
  // This restores the first-mount behaviour of the resolved field, and improves on it for a warm
  // remount: there the resolver itself emitted the husk (its `loaded && !customization` branch
  // cannot fire on a truthy value), so the old binding showed the dead tile in that case.
  //
  // Normalized HERE and not in the reducer on purpose: `state.customization`'s husk shape is
  // load-bearing for the send path (upload.effects.ts#resolveCustomization) and pinned by its own
  // spec, so changing it would ripple well beyond this thumbnail.
  customization$: Observable<Customization | null>;
  customizationLoading: boolean;
  promotionLoading: boolean;
  transfer$: Observable<Transfer>;
  transferCommonParameters$: Observable<TransferCommonParameters>;
  transferPassword$: Observable<string>;
  transferAvailabilityDuration$: Observable<number>;

  transferQueue$: Observable<number>;
  transferQueueLoaded$: Observable<boolean>;
  transferQueueLoading$: Observable<boolean>;

  verifyEmailResponse$: Observable<GetEmailChallengeOutput['emailChallenge']['status']>;
  verifyEmailLoaded$: Observable<boolean>;
  verifyEmailLoading$: Observable<boolean>;
  verifyEmailError$: Observable<any>;

  validateEmailVerificationCodeResponse$: Observable<any>;
  validateEmailVerificationCodeLoaded$: Observable<boolean>;
  validateEmailVerificationCodeLoading$: Observable<boolean>;
  validateEmailVerificationCodeError$: Observable<any>;

  sendEmailVerificationLoading$: Observable<boolean>;
  sendEmailVerificationLoaded$: Observable<boolean>;
  sendEmailVerificationError$: Observable<any>;

  verificationCodeFormDisplayed$: Observable<boolean>;

  uploadStatus$: Observable<UploaderStatus>;
  updateAndResumeUploadLoading$: Observable<boolean>;
  // Single source of truth for the submit button's "busy" look (spinner + disabled), consumed by
  // both the Email and Link submit buttons in the template -- see isUploadStatusBusy for why
  // Queued/Resumable count as busy alongside Starting.
  isSubmitBusy$: Observable<boolean>;
  // Drives the box's '.animate' position-shift class -- deliberately NARROWER than isSubmitBusy$:
  // it must NOT include verifyEmailLoading, or sending the email verification code (unrelated to
  // an actual upload starting) shifts the whole box and un-shifts it again once the verification
  // code screen appears, a visible glitch right at that transition. It also must NOT shift while
  // the 2GB queue modal (ModalSizePremiumComponent) is displayed: a transfer becoming Queued flips
  // both this and modalSizePremiumDisplayed$ at the same instant, so without this guard the box
  // starts sliding left at the exact moment the modal's card pops in and its backdrop fades in on
  // top of it -- the two simultaneous transitions visually collide ("les deux fenetres se
  // telescopent"). The box still renders busy/disabled via isSubmitBusy$ while the modal is up;
  // only the position-shift is deferred until the modal itself is gone.
  isUploadAnimating$: Observable<boolean>;
  drawerExpanded$: Observable<boolean>;
  transferSize$: Observable<any>;
  domain$: Observable<GetRecordOutput['record']>;
  selectedLanguage$: Observable<string>;
  countryCode$: Observable<string>;
  verifyCaptchaResponse$: Observable<string>;
  countries$: Observable<any[]>;
  transferErrorMessage$: Observable<string>;
  modalSizePremiumDisplayed$: Observable<boolean>;
  externalPromotionSelected$: Observable<Promotion>;

  transferSize: any;
  currentDomain: any;
  domain: string;
  configFormStatus: string;
  configPasswordStatus: string;
  drawerExpanded: boolean;
  tabTranslate = {
    Email: 'UPLOADER_EMAIL_TAB_LABEL',
    Link: 'UPLOADER_LINK_TAB_LABEL',
  };
  translatedAvailabilityDuration: string;
  queue: number;
  queueLoaded = false;
  verifyEmailResponse = '';
  subscriptions: Subscription[] = [];

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private dialog: DialogService,
    private resolveCustomizationService: ResolveCustomizationService,
    private resolveExternalPromotionService: ResolveExternalPromotionService,
    private cd: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService

  ) {

    this.customizationLoading = true;
    this.promotionLoading = true;
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.transferCommonParameters$ = this.store$.select(fromUploader.getTransferCommonParameters);
    this.transferPassword$ = this.store$.select(fromUploader.getTransferPassword);
    this.transferAvailabilityDuration$ = this.store$.select(fromUploader.getTransferAvailabilityDuration);
    this.transferQueue$ = this.store$.select(fromUploader.getTransferQueue);

    this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);

    this.selectedLanguage$ = this.store$.select(fromRoot.getSelectedLanguage);
    this.drawerExpanded$ = this.store$.select(fromUploader.getConfigDrawerExpanded);
    this.transferSize$ = this.store$.select(fromUploader.getTransferSize);
    // See the customization$ declaration for why the husk is collapsed to null here.
    this.customization$ = this.store$.select(fromUploader.getCustomization).pipe(
      map(customization => customization?.id ? customization : null),
    );
    this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
    this.countries$ = this.store$.select(fromRoot.getCountries);
    this.countryCode$ = this.store$.select(fromRoot.getCountryCode);
    this.modalSizePremiumDisplayed$ = this.store$.select(fromUploader.getModalSizePremiumDisplayed);

    this.verifyEmailResponse$ = this.store$.select(fromUploader.getVerifyEmailResponse);
    this.verifyEmailLoaded$ = this.store$.select(fromUploader.getVerifyEmailLoaded);
    this.verifyEmailLoading$ = this.store$.select(fromUploader.getVerifyEmailLoading);
    this.verifyEmailError$ = this.store$.select(fromUploader.getVerifyEmailError);

    this.validateEmailVerificationCodeResponse$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeResponse);
    this.validateEmailVerificationCodeLoaded$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeLoaded);
    this.validateEmailVerificationCodeLoading$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeLoading);
    this.validateEmailVerificationCodeError$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeError);

    this.sendEmailVerificationLoaded$ = this.store$.select(fromUploader.getSendEmailVerificationLoaded);
    this.sendEmailVerificationLoading$ = this.store$.select(fromUploader.getSendEmailVerificationLoading);
    this.sendEmailVerificationError$ = this.store$.select(fromUploader.getSendEmailVerificationError);

    this.verificationCodeFormDisplayed$ = this.store$.select(fromUploader.getVerificationCodeFormDisplayed);

    this.updateAndResumeUploadLoading$ = this.store$.select(fromUploader.getUpdateAndResumeUploadLoading);
    this.externalPromotionSelected$ = this.store$.select(fromUploader.getExternalPromotionSelected);

    this.isSubmitBusy$ = combineLatest([this.uploadStatus$, this.updateAndResumeUploadLoading$, this.verifyEmailLoading$]).pipe(
      map(([uploadStatus, updateAndResumeUploadLoading, verifyEmailLoading]) =>
        isUploadStatusBusy(uploadStatus) || updateAndResumeUploadLoading || verifyEmailLoading),
    );

    this.isUploadAnimating$ = combineLatest([this.uploadStatus$, this.updateAndResumeUploadLoading$, this.modalSizePremiumDisplayed$]).pipe(
      map(([uploadStatus, updateAndResumeUploadLoading, modalSizePremiumDisplayed]) =>
        !modalSizePremiumDisplayed && (isUploadStatusBusy(uploadStatus) || updateAndResumeUploadLoading)),
    );

    this.subscriptions.push(
      this.store$.select(fromUploader.getConfigFormStatus).subscribe(status => { this.configFormStatus = status; }),
      this.store$.select(fromUploader.getConfigPasswordStatus).subscribe(status => { this.configPasswordStatus = status; }),
      // Mirrored onto a field for the same reason configFormStatus is: the submit path needs to read it
      // synchronously, and it must know whether the drawer is ALREADY open before toggling it.
      this.drawerExpanded$.subscribe(drawerExpanded => { this.drawerExpanded = drawerExpanded; }),
      this.domain$.subscribe(domain => { this.currentDomain = domain; }),
      combineLatest([this.transferAvailabilityDuration$, this.selectedLanguage$]).subscribe(([availabilityDuration, language]) => {
        moment.locale(language);
        if (language === 'en') {
          this.translatedAvailabilityDuration = moment().add(availabilityDuration, 'seconds').format('ll');
        } else {
          this.translatedAvailabilityDuration = moment().add(availabilityDuration, 'seconds').format('L');
        }
      }),

      // The 2GB queue modal must open on a genuinely queued/resumable transfer regardless of
      // whether the unrelated VAT/countries call succeeds -- it previously opened the modal only
      // AFTER getCountriesLoaded flipped true, but loadCountriesFailure never sets that flag, so
      // a failed countries load left `first(loaded => !!loaded)` waiting forever and the modal
      // never appeared (on top of the button/footer already going idle -- see isUploadStatusBusy).
      // loadCountries() is still dispatched (openModalSizePremium reads its best-effort result
      // below), but it no longer gates the dialog itself.
      this.modalSizePremiumDisplayed$.pipe(distinctUntilChanged()).subscribe((displayed) => {
        if (displayed === true) {
          this.store$.dispatch(EnvironmentActions.loadCountries());
          this.openModalSizePremium();
        }
      }),
      this.verifyEmailResponse$.subscribe(response => {
        if (response === 'Dirty') {
          this.store$.dispatch(PrepareActions.DisplayVerificationCodeForm());
        }
      }),
      this.validateEmailVerificationCodeResponse$.subscribe(async response => {
        if (response === 'Clean') {
          await this.upload();
          this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
        }
      }),
      this.externalPromotionSelected$.subscribe(promotion => this.promotionSelected = promotion),

    );
  }

  ngOnInit() {

    this.subscriptions.push(
      // Kept for its two side effects, NOT for its value: it drives the loadCustomization dispatch
      // chain that fills the store, and it settles customizationLoading (which starts true from the
      // constructor, so the tile does not flash before the data lands). The value itself is no
      // longer captured -- customization$ reads the store, which stays correct after a save.
      this.resolveCustomizationService.resolve().subscribe(() => {
        this.customizationLoading = false;
        this.cd.markForCheck();
      }),
      this.resolveExternalPromotionService.resolve().subscribe(({ selectedPromotion, promotions }) => {
        this.promotionLoading = false;
        this.promotionSelected = selectedPromotion;
        this.promotions = promotions;
        this.cd.markForCheck();
      }),
    )
    if (!this.uploadFormMode && this.transferSettings?.delivery?.options?.length > 0) {
      this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode: this.transferSettings.delivery.options[0] as DeliveryType }));
    }
  }

  // Locked while a submit is in flight. Switching the mode unmounts the active form (this
  // component's template *ngIf on uploadFormMode), including app-link-form -- the SOLE renderer of
  // the inline "this link is already taken" message. Since prepare.component.ts deliberately
  // suppresses both the fatal modal and the /send navigation for CustomUrlAlreadyInUseError, a 409
  // landing after a mid-flight switch would leave NO feedback at all: the exact silent failure the
  // inline-409 handling exists to remove. buildUploadInput() pins the mode at SEND time; this pins
  // it for the whole round-trip, which is what the carve-out actually depends on.
  // Gated on isSubmitBusy$ (not just Starting) so the tabs are inert exactly while the submit
  // buttons already are -- Queued and Resumable included. Mirrors the mobile guard in
  // upload-mobile-link-parameters.component.ts's backToDeliveryTypeSelection().
  async onSelectSmashMode(uploadFormMode: DeliveryType): Promise<void> {
    if (await lastValueFrom(this.isSubmitBusy$.pipe(first()))) {
      return;
    }
    this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
  }

  get emailInput() {
    return this.uploadFormMode === 'Email' ? this.emailForm.emailForm.value.sender.email : this.linkForm.linkForm.value.sender.email;
  }

  async upload() {
    // Checked HERE rather than in handleFormSubmitted, because that is not the only way in: the constructor
    // subscribes to validateEmailVerificationCodeResponse$ and calls upload() directly when the verification
    // code comes back 'Clean'. The drawer stays mounted through that step, so a user could clear the password
    // there and then validate the code, submitting without one. The refusal belongs where every entry point
    // funnels through, so any future caller inherits it instead of reopening the hole.
    if (this.isSettingsDrawerBlockingSubmit()) {
      return;
    }
    // The customization is deliberately NOT re-dispatched here. `this.customization` is the
    // resolver's ONE-SHOT read of the domain default (see the resolve() subscription in ngOnInit);
    // it never listens to the store, so it stays on the default even after the sender picks another
    // background. Re-dispatching it at send time therefore overwrote the choice the Design modal had
    // just written to the store (modal-customization-receiver#save), microseconds before Upload(),
    // and buildUploadInput serialised the DEFAULT into CreateTransfer -- the sender saw the tile
    // ticked and the preview update, then got a download page with the previous background.
    //
    // Nothing is needed in its place, because the component was never the safety net: upload$
    // (store/effects/upload.effects.ts) resolves the customization itself before building the input
    // -- it takes commonParameters.customization when set (seeded by loadCustomizationSuccess$,
    // overwritten by the modal on save), and otherwise falls back to the resolver's value. That
    // fallback is what carries every send after a ResetUpload ("new smash"), which clears
    // commonParameters without clearing the loaded flags. The mobile Email counterpart has run
    // without this re-dispatch all along, on that same fallback.
    //
    // The promotion below IS still re-dispatched: unlike the customization, `promotionSelected` is
    // kept in sync by a store subscription, so re-dispatching it is not a stale write.
    if (this.promotionSelected) {
      this.store$.dispatch(PrepareActions.SetTransferPromotion({ promotion: this.promotionSelected }));
    }

    const uploadStatus = await lastValueFrom(this.uploadStatus$.pipe(first()));
    // Only "update and resume" an upload that is genuinely resumable (Paused/Queued/Resumable).
    // Any other status — including null, Finished or a residual status left after a reset — must
    // start a fresh upload; routing those to UpdateAndResume would hit a null uploader and crash.
    if (shouldResumeUpload(uploadStatus)) {
      this.store$.dispatch(UploadActions.UpdateAndResumeUpload());
    } else {
      this.store$.dispatch(UploadActions.Upload());
    }

    const transferSize = await lastValueFrom(this.transferSize$.pipe(first()));
    this.gtmService.pushTag({
      event: Constant.eventGTM.START_TRANSFER,
      transfer_size: transferSize,
    });
  }

  async verifyEmail() {
    this.store$.dispatch(PrepareActions.VerifyEmail({ username: this.emailInput }));
    const [verifyEmailResponse, verifyEmailError] = await lastValueFrom(this.store$.select(fromUploader.getVerifyEmailResponse)
      .pipe(
        withLatestFrom(this.verifyEmailError$, this.verifyEmailLoading$, this.verifyEmailLoaded$),
        filter(([_, verifyEmailError, verifyEmailLoading, verifyEmailLoaded]) => (verifyEmailLoaded && !verifyEmailLoading) || !!verifyEmailError),
        first(),
      ));

    if (verifyEmailError) {
      return false;
    }

    if (verifyEmailResponse === 'Clean') {
      this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
      return true;
    }
    this.store$.dispatch(PrepareActions.DisplayVerificationCodeForm());
    return false;
  }

  async canByPassEmailVerification() {
    const identity = await lastValueFrom(this.identity$.pipe(first(identity => !!identity)));
    const emailInput = removeTagFromEmail(this.emailInput);
    return identity?.username && identity?.username === emailInput;
  }

  async isLoggedIn() {
    const identity = await lastValueFrom(this.identity$.pipe(first(identity => !!identity)));
    return identity?.username;
  }


  async isVerificationCodeValid() {
    const [validateEmailVerificationCodeResponse, validateEmailVerificationCodeError] = await lastValueFrom(this.validateEmailVerificationCodeResponse$
      .pipe(
        withLatestFrom(this.validateEmailVerificationCodeError$, this.validateEmailVerificationCodeLoading$, this.validateEmailVerificationCodeLoaded$),
        filter(([_, validateEmailVerificationCodeError, validateEmailVerificationCodeLoading, validateEmailVerificationCodeLoaded]) => (validateEmailVerificationCodeLoaded && !validateEmailVerificationCodeLoading) || !!validateEmailVerificationCodeError),
        first(),
      ));
    return validateEmailVerificationCodeResponse === 'Clean' && !validateEmailVerificationCodeError;
  }

  async canSubmitForm() {
    const uploadStatus = await lastValueFrom(this.uploadStatus$.pipe(first()));
    const isVerifyingEmail = await lastValueFrom(this.verifyEmailLoading$.pipe(first()));

    return (!uploadStatus || isValidStatusToStartUpload(uploadStatus)) && !isVerifyingEmail;
  }

  // The settings drawer's own form (ConfigSendParametersComponent#parametersForm) publishes its validity
  // here as configFormStatus, and for a long time exactly one thing read it: whether the drawer may be
  // closed. Nothing stopped a send while it was invalid, which cost nothing as long as none of its
  // validators could realistically fail. That changed with the account policy that FORCES a transfer
  // password: the drawer is invalid until one is typed, and the send went out empty anyway, straight into
  // the backend's PasswordRequiredError -- so the user was told by a server error that a password was
  // missing, for a field the interface had never marked as needed. Requiring it client-side is only half a
  // fix while the form still submits.
  //
  // The drawer is EXPANDED rather than the send silently refused: a button that does nothing, with no
  // visible reason, is worse than the error it replaces. The mobile forms already do this in their own
  // submit(). Gated on canConfigSmash because a domain with no configurable settings renders no drawer at
  // all, so its configFormStatus simply stays null and must not be read as a refusal.
  //
  // The toggle is conditional because that action TOGGLES: firing it on an already-open drawer would
  // close it, hiding the very field the refusal is about -- the same trap the mobile custom-URL card fell
  // into. Reveal only what is hidden.
  // Keyed on the PASSWORD control alone, not on the whole drawer form. The drawer also validates
  // availabilityDuration with min/max against a store-seeded value, and every one of its rows is hidden
  // when its own `active` flag is false -- so a domain can legitimately arrive with a field that is invalid
  // at mount, through no user action, sometimes not even rendered. Gating the send on whole-form validity
  // would turn any such latent invalidity into an account that cannot send at all, with nothing on screen
  // to explain why: strictly worse than the server error being replaced. The policy this client enforces
  // before submitting concerns the password and nothing else, so that is what the gate reads. Whole-form
  // validity still gates whether the drawer may be CLOSED, which is the right rule for a different question.
  private isSettingsDrawerBlockingSubmit(): boolean {
    if (!this.canConfigSmash || this.configPasswordStatus !== 'INVALID') {
      return false;
    }
    if (!this.drawerExpanded) {
      this.store$.dispatch(PrepareActions.toggleConfigDrawerExpanded());
    }
    return true;
  }

  async handleFormSubmitted($event) {
    $event.event.preventDefault();
    $event.event.stopPropagation();
    // Also checked HERE, not only in upload(). Both copies are needed and neither is redundant.
    //
    // upload() is the funnel point, so the check there is what closes the second entry path (the
    // verification-code callback) and any future one. But refusing THERE only would walk a sender through a
    // whole email-verification round -- a code emailed, typed, validated -- before telling them a password is
    // missing, and the callback then hides the verification form unconditionally. So the refusal fires here
    // first, at the click, where the user still has the context of what they just pressed.
    if (this.isSettingsDrawerBlockingSubmit()) {
      return;
    }
    const canSubmitForm = await this.canSubmitForm();
    if (!canSubmitForm) {
      return;
    }

    const canByPassEmailVerification = await this.canByPassEmailVerification();
    if (canByPassEmailVerification) {
      await this.upload();
    } else {
      const isEmailVerified = await this.verifyEmail();
      if (isEmailVerified) {
        await this.upload();
      }
    }
  }

  async openModalSizePremium() {
    // Best-effort snapshot -- NOT gated on the countries list being populated. loadCountries()
    // (dispatched by the caller right before this) may still be in flight or may have failed
    // (loadCountriesFailure), in which case `countries` is simply the empty initial-state array;
    // the modal itself must still open (see the modalSizePremiumDisplayed$ subscription above).
    const countries = await this.countries$.pipe(first()).toPromise();
    const countryCode = await this.countryCode$.pipe(first(countryCode => !!countryCode)).toPromise();
    const queue = await this.transferQueue$.pipe(first(queue => !!queue)).toPromise();
    const transferSize = await this.transferSize$.pipe(first(size => !!size)).toPromise();
    this.dialog.open(ModalSizePremiumComponent,
      {
        closeButton: false,
        enableClose: false,
        size: 'sm',
        data: {
          countries,
          countryCode,
          queue,
          transferSize,
        },
      }
    );
  }

  submitVerificationCode() {
    this.emailValidationComponent.submit();
  }

  async submitForm($event) {
    if (this.uploadFormMode) {
      switch (this.uploadFormMode) {
        case 'Email':
          this.emailForm.onSubmit($event);
          break;
        case 'Link':
          this.linkForm.onSubmit($event);
          break;
        default:
          this.emailForm.onSubmit($event);
          break;
      }
    }
  }

  // The only dedicated open/close control: the close icon, the gear, the summary bar and the drawer's own
  // OK button all call it. Two other paths move the drawer without going through here -- the send refusal
  // above expands it, and resetDrawer collapses it on navigation -- so this is the only place a USER can
  // deliberately toggle it, not the only code that changes its state. The INVALID guard was written to stop the user CLOSING a drawer whose
  // form is not satisfied -- but it was direction-blind and refused to OPEN one just as much.
  //
  // That cost nothing until the forced-password policy started being enforced here: on a fresh mount
  // statusChanges had not fired, minLength passes on an empty string, and the store started at null, so
  // INVALID-while-closed was hard to reach. (Not strictly impossible -- the pre-fix CVA bug could publish a
  // status on mount when a password was already stored, so a stored too-short password could produce it.)
  // The required-password policy makes it the NORMAL mount state on a forcing account. So the gear icon
  // became a control that does nothing -- the same "silently refused, no visible reason" symptom this whole
  // change exists to remove, one click to the left of it.
  //
  // Opening is therefore always allowed; only closing stays gated. That asymmetry is the point: the user
  // can always reach the field, and cannot bury it again without answering it.
  handleOpenCloseDrawer(): void {
    if (!this.canConfigSmash) {
      return;
    }
    if (this.drawerExpanded && this.configFormStatus === 'INVALID') {
      return;
    }
    this.store$.dispatch(PrepareActions.toggleConfigDrawerExpanded());
  }

  handleCloseVerificationCode(): void {
    this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
    this.store$.dispatch(PrepareActions.ResetVerifyEmail());
    this.emailValidationComponent.verificationCodeComponent.reset();
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}
